import SpotifyIcon from "@/fragments/SpotifyIcon";
import TikTokIcon from "@/fragments/TikTokIcon";
import { useUser, useUserSummary } from "@/services/hooks";
import { renderTextWithMentions } from "@/utils/mentions";
import { daysAgo } from "@/utils/time";
import {
  ChatBubbleOutline,
  Instagram,
  PlayArrow,
  Star,
  Verified,
  Block,
  AutoAwesome,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import UserCommentsContainer from "@/components/UserCommentsContainer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { blockUser } from "@/services/api";
import { currentUser } from "@/App";

export default function UserProfileContainer({ userKey }) {
  const { data: user, isLoading } = useUser(userKey);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          height: "80vh",
          py: 4,
          width: "100%",
        }}
      >
        {isLoading ? (
          <CircularProgress sx={{ mt: 4 }} />
        ) : (
          <Stack direction="column" gap={4} sx={{ width: "100%", pb: 10 }}>
            <UserBio user={user} />
            <UserSummary user={user} />
            <UserStats user={user} />
            <UserCommentsContainer user={user} />
            <BlockUser user={user} />
          </Stack>
        )}
        {/*  */}
        {/* <UserComments user={user} /> */}
      </Box>
    </Container>
  );
}

function BlockUser({ user }) {
  if (currentUser.role !== "artist") return null;

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: blockUser,
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: ["user", user.key] });
      // queryClient.invalidateQueries({ queryKey: ["comments"] });
      window.location.reload();
    },
  });

  const handleBlockUser = () => {
    const confirmed = window.confirm(
      "Are you sure you want to block this user? User comments will be removed, subscriptions will be cancelled, and user will be blocked from Notebook. This action cannot be undone."
    );
    if (confirmed) {
      mutation.mutate(user.key);
    }
  };

  return (
    <Box>
      <Button
        variant="outlined"
        color="error"
        startIcon={<Block />}
        loading={mutation.isPending}
        disabled={user.blocked || mutation.isPending}
        onClick={handleBlockUser}
        fullWidth
        sx={{
          borderRadius: "25px",
        }}
      >
        {user.blocked ? "User Blocked" : "Block User"}
      </Button>
    </Box>
  );
}

function UserBio({ user }) {
  return (
    <Stack direction="column" gap={2} alignItems="center" width="100%">
      <Avatar src={user?.meta.profile} sx={{ width: 150, height: 150 }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">{user?.name}</Typography>

        <UserBadge user={user} />
        {user.meta.instagram && (
          <IconButton onClick={() => window.open(user.meta.instagram)}>
            <Instagram />
          </IconButton>
        )}
        {user.meta.spotify && (
          <IconButton onClick={() => window.open(user.meta.spotify)}>
            <SpotifyIcon size={20} />
          </IconButton>
        )}
        {user.meta.tiktok && (
          <IconButton onClick={() => window.open(user.meta.tiktok)}>
            <TikTokIcon size={20} />
          </IconButton>
        )}
        <Chip
          label={daysAgo(user.created) + " days"}
          size="small"
          variant="outlined"
          sx={{ color: "lightgray", py: 1.2 }}
        />
      </Stack>
      {user?.meta?.bio?.length > 0 && (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {user?.meta.bio?.split("\n").map((line, index) => (
            <Typography key={index} variant="body1" sx={{ my: 0.4 }}>
              {renderTextWithMentions(line)}
            </Typography>
          ))}
        </Box>
      )}
    </Stack>
  );
}

function UserStats({ user }) {
  return (
    <Card
      sx={{
        width: "100%",
        p: 2,
        borderRadius: "10px",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          <PlayArrow color="primary" />
          {user.stats.play_count}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          <Star color="primary" />
          {user.stats.favorite_count}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          <ChatBubbleOutline color="primary" />
          {user.stats.comment_count}
        </Box>
      </Stack>
    </Card>
  );
}

function UserBadge({ user, style }) {
  return (
    <>
      {user?.role === "artist" && (
        <Verified
          color={user.verified ? "secondary" : "disabled"}
          style={style}
        />
      )}

      {user?.role === "user" && user?.verified && (
        <Verified color="primary" style={style} />
      )}
    </>
  );
}

function UserSummary({ user }) {
  if (currentUser.role !== "artist") return null;

  const { data: userSummary, isLoading } = useUserSummary(user.key);

  return (
    <Card sx={{ width: "100%", p: 2, borderRadius: "10px", minHeight: 100 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoAwesome sx={{ fontSize: "1.0rem", color: "#04FFFF" }} />
        <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>
          AI Summary
        </Typography>
      </Stack>
      {isLoading ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Thinking...
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {userSummary?.summary}
        </Typography>
      )}
    </Card>
  );
}
